import React, { useState, useEffect, useMemo } from 'react';
import { apiService } from '../../utils/api';
import { UserProfile, UpdateProfileRequest } from '../../types/api';
import { ProfileTabs, ProfileTab } from '../../components/ProfileTabs';
import { MyInquiries } from './MyInquiries';
import styles from './Profile.module.css';

function parseInputToArray(input: string): string[] {
  return input
    .split(/[,;\n]/g)
    .map(item => item.trim())
    .filter(Boolean);
}

export const Profile: React.FC = () => {
  const [, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // State for active tab
  const [activeTab, setActiveTab] = useState<ProfileTab>('settings');

  // Form state
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    country: '',
    company_name: '',
    job_title: '',
    x_account: '',
    linkedin: '',
    preferred_investment_amount_min: '',
    preferred_investment_amount_max: '',
    industries_of_interest: [] as string[]
  });

  // Separate input fields for companies of interest
  const [buyInput, setBuyInput] = useState('');
  const [sellInput, setSellInput] = useState('');

  // Parse the input fields to arrays
  const buyParsed = useMemo(() => parseInputToArray(buyInput), [buyInput]);
  const sellParsed = useMemo(() => parseInputToArray(sellInput), [sellInput]);

  // Available industries (you can expand this list)
  const availableIndustries = [
    'Technology',
    'Healthcare',
    'Finance',
    'Energy',
    'Consumer Goods',
    'Real Estate',
    'Manufacturing',
    'Transportation',
    'Education',
    'Entertainment'
  ];

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      // Note: You'll need to add a getUserProfile method to apiService
      // For now, we'll use a placeholder
      const userProfile = await apiService.getUserProfile();
      setProfile(userProfile);
      
      // Populate form with existing data
      setFormData({
        first_name: userProfile.first_name || '',
        last_name: userProfile.last_name || '',
        email: userProfile.email || '',
        country: userProfile.country || '',
        company_name: userProfile.company_name || '',
        job_title: userProfile.job_title || '',
        x_account: userProfile.x_account || '',
        linkedin: userProfile.linkedin || '',
        preferred_investment_amount_min: userProfile.preferred_investment_amount_min?.toString() || '',
        preferred_investment_amount_max: userProfile.preferred_investment_amount_max?.toString() || '',
        industries_of_interest: userProfile.industries_of_interest || []
      });

      // Populate company interest input fields
      setBuyInput(userProfile.companies_of_interest_buy?.join(', ') || '');
      setSellInput(userProfile.companies_of_interest_sell?.join(', ') || '');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleIndustryToggle = (industry: string) => {
    setFormData(prev => ({
      ...prev,
      industries_of_interest: prev.industries_of_interest.includes(industry)
        ? prev.industries_of_interest.filter(i => i !== industry)
        : [...prev.industries_of_interest, industry]
    }));
  };

  const selectAll = () => setFormData(prev => ({
    ...prev,
    industries_of_interest: availableIndustries.slice()
  }));

  const clearAll = () => setFormData(prev => ({
    ...prev,
    industries_of_interest: []
  }));

  // Handle tab change
  const handleTabChange = (tab: ProfileTab) => {
    setActiveTab(tab);
  };

  // Handle logout
  const handleLogout = () => {
    // Clear local storage
    localStorage.removeItem('authToken');
    
    // Redirect to login
    window.location.href = '/login';
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const updateData: UpdateProfileRequest = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        country: formData.country,
        company_name: formData.company_name,
        job_title: formData.job_title,
        x_account: formData.x_account,
        linkedin: formData.linkedin,
        preferred_investment_amount_min: formData.preferred_investment_amount_min ? Number(formData.preferred_investment_amount_min) : undefined,
        preferred_investment_amount_max: formData.preferred_investment_amount_max ? Number(formData.preferred_investment_amount_max) : undefined,
        companies_of_interest_buy: buyParsed,
        companies_of_interest_sell: sellParsed,
        industries_of_interest: formData.industries_of_interest
      };

      await apiService.updateUserProfile(updateData);
      setSuccess('Profile updated successfully!');
      
      // Refresh profile data
      await fetchProfile();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading profile...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Profile</h1>
      <p className={styles.description}>Manage your personal information and investment preferences.</p>

      <ProfileTabs activeTab={activeTab} onTabChange={handleTabChange} />

      {error && (
        <div className={styles.error}>
          {error}
        </div>
      )}

      {success && (
        <div className={styles.success}>
          {success}
        </div>
      )}

      {activeTab === 'settings' && (
        <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Personal Information</h2>
          
          <div className={styles.formGroup}>
            <label htmlFor="first_name">First Name</label>
            <input
              type="text"
              id="first_name"
              name="first_name"
              value={formData.first_name}
              onChange={handleInputChange}
              className={styles.input}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="last_name">Last Name</label>
            <input
              type="text"
              id="last_name"
              name="last_name"
              value={formData.last_name}
              onChange={handleInputChange}
              className={styles.input}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={styles.input}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="country">Country</label>
            <input
              type="text"
              id="country"
              name="country"
              value={formData.country}
              onChange={handleInputChange}
              className={styles.input}
            />
          </div>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Professional Information</h2>
          
          <div className={styles.formGroup}>
            <label htmlFor="company_name">Company</label>
            <input
              type="text"
              id="company_name"
              name="company_name"
              value={formData.company_name}
              onChange={handleInputChange}
              className={styles.input}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="job_title">Job Title</label>
            <input
              type="text"
              id="job_title"
              name="job_title"
              value={formData.job_title}
              onChange={handleInputChange}
              className={styles.input}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="x_account">X (Twitter) Account</label>
            <input
              type="text"
              id="x_account"
              name="x_account"
              value={formData.x_account}
              onChange={handleInputChange}
              className={styles.input}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="linkedin">LinkedIn</label>
            <input
              type="text"
              id="linkedin"
              name="linkedin"
              value={formData.linkedin}
              onChange={handleInputChange}
              className={styles.input}
            />
          </div>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Investment Preferences</h2>
          
          <div className={styles.formGroup}>
            <label htmlFor="preferred_investment_amount_min">Minimum Investment Amount ($)</label>
            <input
              type="number"
              id="preferred_investment_amount_min"
              name="preferred_investment_amount_min"
              value={formData.preferred_investment_amount_min}
              onChange={handleInputChange}
              className={styles.input}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="preferred_investment_amount_max">Maximum Investment Amount ($)</label>
            <input
              type="number"
              id="preferred_investment_amount_max"
              name="preferred_investment_amount_max"
              value={formData.preferred_investment_amount_max}
              onChange={handleInputChange}
              className={styles.input}
            />
          </div>

          <div className={styles.formGroup}>
            <label>Industries of Interest</label>
            <div className={styles.toolbar}>
              <button type="button" className={`${styles.button} ${styles.secondary}`} onClick={selectAll}>Select All</button>
              <button type="button" className={`${styles.button} ${styles.secondary}`} onClick={clearAll}>Clear All</button>
            </div>
            <div className={styles.industriesGrid}>
              {availableIndustries.map(industry => (
                <button
                  key={industry}
                  type="button"
                  className={`${styles.industryOval} ${formData.industries_of_interest.includes(industry) ? styles.selected : ''}`}
                  onClick={() => handleIndustryToggle(industry)}
                  disabled={saving}
                >
                  {industry}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="companies_of_interest_buy">Companies of Interest (Buy)</label>
            <textarea
              id="companies_of_interest_buy"
              value={buyInput}
              onChange={(e) => setBuyInput(e.target.value)}
              className={styles.textarea}
              placeholder="e.g., Apple, Microsoft, Google (comma, semicolon, or new lines)"
              disabled={saving}
            />
            {buyParsed.length > 0 && (
              <div className={styles.chips}>
                {buyParsed.map(item => (
                  <span key={`buy-${item}`} className={styles.chip}>{item}</span>
                ))}
              </div>
            )}
            <div className={styles.hint}>Optional</div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="companies_of_interest_sell">Companies of Interest (Sell)</label>
            <textarea
              id="companies_of_interest_sell"
              value={sellInput}
              onChange={(e) => setSellInput(e.target.value)}
              className={styles.textarea}
              placeholder="e.g., Tesla, Meta, Netflix (comma, semicolon, or new lines)"
              disabled={saving}
            />
            {sellParsed.length > 0 && (
              <div className={styles.chips}>
                {sellParsed.map(item => (
                  <span key={`sell-${item}`} className={styles.chip}>{item}</span>
                ))}
              </div>
            )}
            <div className={styles.hint}>Optional</div>
          </div>
        </div>

        <div className={styles.formActions}>
          <button
            type="button"
            onClick={handleLogout}
            className={styles.logoutButton}
          >
            Logout
          </button>
          <button
            type="submit"
            disabled={saving}
            className={styles.saveButton}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
      )}

      {activeTab === 'inquiries' && (
        <MyInquiries />
      )}
    </div>
  );
};
